/**
 *  @class
 *  @function ShippingEstimator
 */
if (!customElements.get('shipping-estimator')) {
  class ShippingEstimator extends HTMLElement {
    constructor() {
      super();
    }

    // === Persistence helpers (minimal) ===
    get storageKey() {
      return `se:${this.dataset.formId || "default"}:v1`;
    }
    readState() {
      try {
        const country = this.querySelector(".shipping-estimator-country");
        const province = this.querySelector(".shipping-estimator-province");
        const zip = this.querySelector(".shipping-estimator-zip");
        return {
          country: country ? country.value : "",
          province: province ? province.value : "",
          zip: zip ? zip.value : "",
        };
      } catch (e) {
        return null;
      }
    }
    saveState() {
      try {
        const st = this.readState();
        if (st) localStorage.setItem(this.storageKey, JSON.stringify(st));
      } catch (e) {}
    }
    loadState() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }
    applyState(state) {
      if (!state) return false;
      // country -> set then dispatch change to populate provinces
      const country = this.querySelector(".shipping-estimator-country");
      if (country && state.country && country.value !== state.country) {
        country.value = state.country;
        try {
          country.dispatchEvent(new Event("change", { bubbles: true }));
        } catch (e) {}
      }
      // zip -> set value and attribute (for cloned nodes)
      const zip = this.querySelector(".shipping-estimator-zip");
      if (zip) {
        const v = state.zip || "";
        if (document.activeElement !== zip && zip.value !== v) {
          zip.value = v;
        }
        if (zip.getAttribute("value") !== v) {
          try {
            zip.setAttribute("value", v);
          } catch (e) {}
        }
      }
      // province -> retry until options populated
      let tries = 0;
      const tryProvince = () => {
        const prov = this.querySelector(".shipping-estimator-province");
        if (prov && state.province) {
          const ok = Array.from(prov.options || []).some(
            (o) => o.value === state.province || o.text === state.province
          );
          if (ok) prov.value = state.province;
        }
        if (tries++ < 40) setTimeout(tryProvince, 120);
      };
      tryProvince();
      return true;
    }
    bindStateListeners() {
      // Save on input/change within the component
      this.addEventListener(
        "change",
        (e) => {
          if (e.target && e.target.matches && e.target.matches("select, input"))
            this.saveState();
        },
        true
      );
      this.addEventListener(
        "input",
        (e) => {
          if (
            e.target &&
            e.target.matches &&
            e.target.matches(".shipping-estimator-zip")
          )
            this.saveState();
        },
        true
      );
    }

    connectedCallback() {
      this.setupCountries();
      this.submitButton = this.querySelector('[type="button"]');
      if (this.submitButton) {
        this.submitButton.addEventListener(
          "click",
          this.estimateShipping.bind(this)
        );
      }
      this.results = this.querySelector(".shipping-estimator--results");

      // state persistence
      this.bindStateListeners();
      const st = this.loadState();
      if (st) {
        // next tick after CountryProvinceSelector init
        setTimeout(() => this.applyState(st), 0);
      }
    }
    async estimateShipping() {
      const zip = this.querySelector(".shipping-estimator-zip").value,
        country = this.querySelector(".shipping-estimator-country").value,
        province = this.querySelector(".shipping-estimator-province").value;

      this.submitButton.classList.add("loading");
      const response = await fetch(
        `${window.theme.routes.cart_url}/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`,
        {
          method: "POST",
        }
      );
      if (response.ok) {
        const shippingRates = await this.getShippingRates(
          zip,
          country,
          province
        );
        this.formatShippingRates(shippingRates);
      } else {
        const jsonError = await response.json();
        this.formatError(jsonError);
      }
      this.submitButton.classList.remove("loading");
      this.saveState();
    }
    async getShippingRates(zip, country, province) {
      const response = await fetch(
        `${window.theme.routes.cart_url}/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`
      );
      const responseText = await response.text();
      if (responseText === "null") {
        return this.getShippingRates(zip, country, province);
      } else {
        return JSON.parse(responseText).shipping_rates;
      }
    }
    formatShippingRates(shippingRates) {
      let formattedShippingRates = "",
        answer = "",
        format =
          window.theme.settings.money_with_currency_format || "${{amount}}";

      // ▼▼▼ ここから修正・追加 ▼▼▼

      // 1. 現在の為替レートを取得 (存在しない場合は1倍とする)
      const currencyRate =
        window.Shopify.currency && window.Shopify.currency.rate
          ? window.Shopify.currency.rate
          : 1;

      shippingRates.forEach((shippingRate) => {
        // 2. 文字列の価格を数値に変換
        const basePrice = parseFloat(shippingRate.price);

        // 3. レートを掛けて現地通貨の価格にし、その場で切り上げる
        // const ではなく直接 Math.ceil を適用して代入します
        const convertedPrice = Math.ceil(basePrice * currencyRate);

        // 4. formatMoneyは通常「セント単位(100倍)」を期待するため100倍する
        const finalValue = convertedPrice * 100;

        // 名前と価格を別々の span タグで囲みます
        formattedShippingRates += `<li>
    <span class="shipping-method-name">${shippingRate.presentment_name}</span>
    <span class="shipping-method-price">${formatMoney(
      finalValue,
      format
    )}</span>
  </li>`;
      });

      // ▲▲▲ ここまで修正・追加 ▲▲▲

      if (shippingRates.length === 0) {
        answer = window.theme.strings.shippingEstimatorNoResults;
      } else if (shippingRates.length === 1) {
        answer = window.theme.strings.shippingEstimatorOneResult;
      } else {
        answer = window.theme.strings.shippingEstimatorMultipleResults;
      }
      const html = `
        <p>${answer}</p>
        ${
          formattedShippingRates === ""
            ? ""
            : `<ul>${formattedShippingRates}</ul>`
        }
      `;
      this.results.innerHTML = "";
      this.results.insertAdjacentHTML("beforeend", html);
      const st = this.loadState();
      if (st) this.applyState(st);
    }
    formatError(errors) {
      let formattedShippingRates = "";
      Object.keys(errors).forEach((errorKey) => {
        formattedShippingRates += `<li>${errorKey} ${errors[errorKey]}</li>`;
      });
      const html = `
        <p>${window.theme.strings.shippingEstimatorError}</p>
        <ul>${formattedShippingRates}</ul>
      `;
      this.results.innerHTML = "";
      this.results.insertAdjacentHTML("beforeend", html);
      const st = this.loadState();
      if (st) this.applyState(st);
    }
    setupCountries() {
      if (Shopify && Shopify.CountryProvinceSelector) {
        let formId = this.dataset.formId;
        new Shopify.CountryProvinceSelector(
          `shipping-estimator-country-${formId}`,
          `shipping-estimator-province-${formId}`,
          {
            hideElement: `shipping-estimator-province-wrapper-${formId}`,
          }
        );
        const st = this.loadState && this.loadState();
        if (st) {
          setTimeout(() => this.applyState(st), 50);
        }
      }
    }
  }
  customElements.define('shipping-estimator', ShippingEstimator);
}
